import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { Section, Container, SectionTitle } from '../../ui';
import { PuzzleIcon, GearsIcon, CheckIcon } from '../../icons';
import styles from './BuybackValueProp.module.css';

const icons = [PuzzleIcon, GearsIcon, TrendingUp];

interface BlockItem {
  title: string;
  badge: string;
  text: string;
  highlights: string[];
}

export function BuybackValueProp() {
  const { t } = useTranslation('buyback');
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const blocks = t('valueProp.items', { returnObjects: true }) as BlockItem[];

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Section variant="light">
      <Container>
        <SectionTitle
          title={t('valueProp.title')}
          subtitle={t('valueProp.subtitle')}
          centered
        />
        <div ref={ref} className={`${styles.rows} ${isVisible ? styles.visible : ''}`}>
          {blocks.map((block, i) => {
            const Icon = icons[i];
            const reversed = i % 2 !== 0;
            const isLucide = i === 2;
            return (
              <div
                key={i}
                className={`${styles.row} ${reversed ? styles.reversed : ''}`}
                style={{ '--row-index': i } as React.CSSProperties}
              >
                <div className={styles.textSide}>
                  <span className={styles.badge}>{block.badge}</span>
                  <div className={styles.titleGroup}>
                    <div className={styles.iconWrap}>
                      {isLucide ? (
                        <Icon size={20} strokeWidth={1.5} color="var(--cyan-technique)" />
                      ) : (
                        <Icon size={20} color="var(--cyan-technique)" />
                      )}
                    </div>
                    <h3 className={styles.rowTitle}>{block.title}</h3>
                  </div>
                  <p className={styles.rowText}>{block.text}</p>
                  <ul className={styles.highlights}>
                    {block.highlights.map((h, j) => (
                      <li key={j} className={styles.highlightItem}>
                        <CheckIcon size={16} color="var(--cyan-technique)" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.illustrationSide}>
                  <div className={styles.iconPlaceholder}>
                    {isLucide ? (
                      <Icon size={64} strokeWidth={1} color="var(--cyan-technique)" />
                    ) : (
                      <Icon size={64} color="var(--cyan-technique)" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
